<script setup>
// §8.1 SideNavGroup — collapsible; indented sub-items with a left hairline.
// Sub-nav active label uses accent-text (the AA-safe on-light brass, §3.2).
//
// When `url` is set, the group has a landing page of its own: the label
// becomes a router-link (selectable, navigates and expands the group) while
// a separate chevron button keeps expand/collapse independent of navigation.
// Without `url`, the whole header remains a plain toggle (no page to go to).
//
// The optional `#trailing` slot renders after the label (like SideNavItem's) and is given the
// group's `open` state: `<template #trailing="{ open }">`. Use it for a count badge that should
// show only while the group is collapsed, so the items inside can't be missed.
import { ref, onMounted } from 'vue'
import LIcon from '../base/Icon.vue'
import globalStore from "/globalStore.js";

const props = defineProps({
	label: { type: String, required: true },
	defaultOpen: { type: Boolean, default: false },
	item: { type: Object, required: true },
	url: { type: String, default: null },
	active: { type: Boolean, default: false },
})
const open = ref(props.defaultOpen)


const init = () => {
	const savedExpandState = getTopLevelWithChildrenState(props.item);
	if (savedExpandState == 'expanded') open.value = true;
	if (savedExpandState == 'collapsed') open.value = false;
};

/**
 * Clicking the label navigates to the group's own page (via the router-link
 * `to`) and, separately, expands the group so its children are visible too —
 * it never collapses on click, only the chevron does that.
 */
const onLabelClick = () => {
	if (open.value) return;

	open.value = true;
	recordTopLevelWithChildrenState(props.item, "expanded");
};


const toggle = () => {
	open.value = !open.value;

	let newSaveState = "";
	if (open.value) newSaveState = "expanded";
	if (!open.value) newSaveState = "collapsed";

	console.log("   toggle(): ", "open.value=", open.value, " | newSaveState=", newSaveState);

	recordTopLevelWithChildrenState(props.item, newSaveState);
};


const getTopLevelExpandingMenuItemKey = (item) =>
{
	const menuItemKey = item.module + "_" + item.text;
	return menuItemKey;
};

const recordTopLevelWithChildrenState = (item, state) =>
{
	console.log("   recordTopLevelWithChildrenState(): ", "item=", item, " | state=", state);

	const storageKey = getStorageKey(item);
	const value = state;

	globalStore.localStorage.saveDataToStorage(storageKey, value);
};


const getTopLevelWithChildrenState = (item) =>
{
	const storageKey = getStorageKey(item);

	let value = globalStore.localStorage.getDataFromStorage(storageKey);

	if (!value) value = "-";	// Not saved yet

	return value;
};


const getStorageKey = (item) => {
	const menuItemKey = getTopLevelExpandingMenuItemKey(item);
	const storageKey = "primary-nav:" + menuItemKey + ":state";
	return storageKey;
};

onMounted(() => {
	init();
});

</script>





<template>
	<div class="l-navgroup">
		<div class="head" :class="{ on: active }">
			<router-link v-if="url" :to="url" class="head-label" :aria-current="active ? 'page' : undefined" @click="onLabelClick">
				<span class="ic">
					<slot name="icon" />
				</span>
				<span class="lbl">
					{{ label }}
				</span>
				<span v-if="$slots.trailing" class="trail">
					<slot name="trailing" :open="open" />
				</span>
			</router-link>

			<button v-else class="head-label as-toggle" type="button" :aria-expanded="open" @click="toggle">
				<span class="ic">
					<slot name="icon" />
				</span>
				<span class="lbl">
					{{ label }}
				</span>
				<span v-if="$slots.trailing" class="trail">
					<slot name="trailing" :open="open" />
				</span>
			</button>

			<button v-if="url" class="chev-btn" type="button" :aria-expanded="open" aria-label="Expand or collapse this section" @click="toggle">
				<LIcon name="chevron-right" class="chev" :class="{ open }" :size="10" />
			</button>
			<span v-else class="tr" aria-hidden="true">
				<LIcon name="chevron-right" class="chev" :class="{ open }" :size="10" />
			</span>
		</div>
		<div v-show="open" class="items">
			<slot />
		</div>
	</div>
</template>





<style scoped>
.l-navgroup
{
	display: flex;
	flex-direction: column;
}

.head
{
	display: flex;
	align-items: center;
	gap: 2px;
	font-size: var(--text-nav);
	font-weight: 600;
	color: var(--text-2);
	border-radius: var(--r);
	width: 100%;
	position: relative;
}

.head-label
{
	display: flex;
	align-items: center;
	gap: 11px;
	padding: 9px 12px;
	flex: 1;
	min-height: var(--target-min);
	background: none;
	border: 0;
	cursor: pointer;
	text-align: left;
	border-radius: var(--r);
	color: inherit;
	font: inherit;
	text-decoration: none;
	min-width: 0;
}

.head-label:hover,
.chev-btn:hover
{
	color: var(--text);
}

.head-label.as-toggle
{
	width: 100%;
}

.chev-btn
{
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: 30px;
	height: var(--target-min);
	background: none;
	border: 0;
	border-radius: var(--r);
	color: var(--text-2);
	cursor: pointer;
}

.chev
{
	color: var(--icon);
	transition: transform var(--t-fast) var(--ease);
}

.chev.open
{
	transform: rotate(90deg);
}

.ic
{
	display: inline-flex;
	flex: none;
	color: var(--icon);
	font-size: 16px;
	width: 17px;
	justify-content: center;
}

.lbl
{
	flex: 1;
}

/* The optional `#trailing` slot (a count badge, say), after the label like SideNavItem's. The
   slot receives `open`, so a consumer can show it only while the group is collapsed. */
.trail
{
	display: inline-flex;
	flex: none;
}

.tr
{
	display: inline-flex;
	margin-left: auto;
	flex: none;
	width: 30px;
	justify-content: center;
}

/* active — same four-signal treatment as SideNavItem's `.on` */
.head.on .head-label
{
	font-weight: 600;
	color: var(--text);
	background: var(--accent-fill);
}

.head.on .ic
{
	color: var(--accent-edge);
}

.head.on::before
{
	content: '';
	position: absolute;
	left: -10px;
	top: 7px;
	bottom: 7px;
	width: 3px;
	border-radius: 0 3px 3px 0;
	background: var(--accent);
}

.items
{
	display: flex;
	flex-direction: column;
	gap: 1px;
	margin-left: 19px;
	padding-left: 11px;
	border-left: 1px solid var(--line-soft);
}

.items :deep(.subnav)
{
	font-size: 12.5px;
}
</style>
